import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaFilter, FaSearch, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, collections } from '../../firebase.marketplace';

const FilterContainer = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.03) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(74, 144, 226, 0.05) 0%, rgba(74, 144, 226, 0) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  
  &:hover {
    &::before {
      opacity: 1;
    }
  }
  
  @media (max-width: 992px) {
    margin-bottom: 1.5rem;
  }
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 1rem;
  
  h3 {
    color: white;
    font-size: 1.3rem;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    background: linear-gradient(90deg, #fff, #e0e0e0);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    
    svg {
      color: #4a90e2;
      filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
    }
  }
  
  @media (max-width: 768px) {
    h3 {
      font-size: 1.2rem;
    }
  }
`;

const ToggleButton = styled(motion.button)`
  background: rgba(74, 144, 226, 0.1);
  border: 1px solid rgba(74, 144, 226, 0.3);
  color: #4a90e2;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    background: rgba(74, 144, 226, 0.2);
    border-color: rgba(74, 144, 226, 0.5);
  }
  
  svg {
    margin-left: 0.5rem;
    font-size: 0.8rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.35rem 0.7rem;
  }
`;

const FilterContent = styled(motion.div)`
  overflow: hidden;
`;

const FilterGroup = styled.div`
  margin-bottom: 1.8rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding-bottom: 1.2rem;
  }
`;

const FilterGroupTitle = styled.h4`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.05rem;
  margin: 0 0 1.2rem 0;
  font-weight: 600;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 16px;
    background: linear-gradient(to bottom, #4a90e2, #357abd);
    margin-right: 8px;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 0 0 1rem 0;
  }
`;

const PriceRange = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const PriceInput = styled.input`
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 0.7rem;
  color: white;
  width: 100%;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) inset;
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    background: rgba(74, 144, 226, 0.05);
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem;
    font-size: 0.9rem;
  }
`;

const Separator = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  padding: 0 0.5rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 0.5rem 0.2rem;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  input {
    margin-right: 0.85rem;
    cursor: pointer;
    accent-color: #4a90e2;
    width: 18px;
    height: 18px;
    position: relative;
    
    &:checked + span {
      color: #4a90e2;
      font-weight: 500;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.35rem 0.5rem;
    
    input {
      width: 16px;
      height: 16px;
      margin-right: 0.75rem;
    }
  }
`;

const ApplyButton = styled(motion.button)`
  width: 100%;
  padding: 0.85rem;
  background: linear-gradient(90deg, #4a90e2, #357abd);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1.8rem;
  box-shadow: 0 4px 15px rgba(74, 144, 226, 0.3);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  letter-spacing: 0.02em;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
  }
  
  &:hover {
    box-shadow: 0 6px 20px rgba(74, 144, 226, 0.4);
    transform: translateY(-2px);
    
    &::before {
      left: 100%;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.95rem;
    border-radius: 10px;
    margin-top: 1.5rem;
  }
`;

const ClearButton = styled(motion.button)`
  width: 100%;
  padding: 0.85rem;
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.85rem;
  transition: all 0.3s ease;
  font-weight: 500;
  
  svg {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: white;
    border-color: rgba(255, 255, 255, 0.25);
    
    svg {
      color: white;
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.95rem;
    border-radius: 10px;
  }
`;

const FilterPanel = ({ onApplyFilters }) => {
  const [expanded, setExpanded] = useState(true);
  const [categories, setCategories] = useState([]);
  const [artists, setArtists] = useState([]);
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    categories: [],
    artists: []
  });

  useEffect(() => {
    loadCategories();
    loadArtists();
  }, []);

  const loadCategories = async () => {
    try {
      // This is a simplified approach - in a real app, you might have a separate collection for categories
      const productsRef = collection(db, collections.PRODUCTS);
      const q = query(productsRef, where('status', '==', 'active'));
      const querySnapshot = await getDocs(q);
      
      const uniqueCategories = new Set();
      querySnapshot.forEach(doc => {
        const product = doc.data();
        if (product.category) {
          uniqueCategories.add(product.category);
        }
      });
      
      setCategories(Array.from(uniqueCategories).sort());
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadArtists = async () => {
    try {
      const artistsRef = collection(db, collections.USERS);
      const querySnapshot = await getDocs(artistsRef);
      
      const artistsList = [];
      querySnapshot.forEach(doc => {
        const artist = doc.data();
        
        // Skip super admins - don't include them in the filter list
        if (artist.roles && artist.roles.superadmin) {
          return; // Skip this iteration
        }
        
        // Only include users who are artists (have the artist role)
        if (artist.roles && artist.roles.artist) {
          artistsList.push({
            id: doc.id,
            name: artist.displayName || artist.email?.split('@')[0] || 'Artist'
          });
        }
      });
      
      setArtists(artistsList.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error loading artists:', error);
    }
  };

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    
    setFilters(prev => {
      if (checked) {
        return {
          ...prev,
          [name]: [...prev[name], value]
        };
      } else {
        return {
          ...prev,
          [name]: prev[name].filter(item => item !== value)
        };
      }
    });
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
  };

  const handleClearFilters = () => {
    setFilters({
      minPrice: '',
      maxPrice: '',
      categories: [],
      artists: []
    });
    onApplyFilters(null);
  };

  return (
    <FilterContainer>
      <FilterHeader>
        <h3><FaFilter /> Filter Products</h3>
        <ToggleButton onClick={handleToggleExpand}>
          {expanded ? 'Hide Filters' : 'Show Filters'}
          {expanded ? <FaChevronUp /> : <FaChevronDown />}
        </ToggleButton>
      </FilterHeader>
      
      <FilterContent
        animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <FilterGroup>
          <FilterGroupTitle>Price Range</FilterGroupTitle>
          <PriceRange>
            <PriceInput
              type="number"
              name="minPrice"
              placeholder="Min"
              value={filters.minPrice}
              onChange={handleInputChange}
            />
            <Separator>to</Separator>
            <PriceInput
              type="number"
              name="maxPrice"
              placeholder="Max"
              value={filters.maxPrice}
              onChange={handleInputChange}
            />
          </PriceRange>
        </FilterGroup>
        
        {categories.length > 0 && (
          <FilterGroup>
            <FilterGroupTitle>Categories</FilterGroupTitle>
            <CheckboxGroup>
              {categories.map(category => (
                <CheckboxItem key={category}>
                  <input
                    type="checkbox"
                    name="categories"
                    value={category}
                    checked={filters.categories.includes(category)}
                    onChange={handleCheckboxChange}
                  />
                  {category}
                </CheckboxItem>
              ))}
            </CheckboxGroup>
          </FilterGroup>
        )}
        
        {artists.length > 0 && (
          <FilterGroup>
            <FilterGroupTitle>Artists</FilterGroupTitle>
            <CheckboxGroup>
              {artists.map(artist => (
                <CheckboxItem key={artist.id}>
                  <input
                    type="checkbox"
                    name="artists"
                    value={artist.id}
                    checked={filters.artists.includes(artist.id)}
                    onChange={handleCheckboxChange}
                  />
                  {artist.name}
                </CheckboxItem>
              ))}
            </CheckboxGroup>
          </FilterGroup>
        )}
        
        <ApplyButton
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleApplyFilters}
        >
          Apply Filters
        </ApplyButton>
        
        <ClearButton
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleClearFilters}
        >
          <FaTimes /> Clear All Filters
        </ClearButton>
      </FilterContent>
    </FilterContainer>
  );
};

export default FilterPanel;
